import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Checkbox,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  Grid,
  HStack,
  Image,
  Text,
} from "@chakra-ui/react";
import RichTextEditor from "../components/RichTextEditor";
import { EventoFormData } from "../types/Event";
import { eventService } from "../services/eventService";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Compress image to be under 5MB
const compressImage = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      // Calculate new dimensions (max 2000px on any side)
      let { width, height } = img;
      const maxDimension = 2000;
      
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height / width) * maxDimension;
          width = maxDimension;
        } else {
          width = (width / height) * maxDimension;
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      // Try different quality levels until under 5MB
      const tryCompress = (quality: number) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Errore durante la compressione dell'immagine"));
              return;
            }

            if (blob.size <= MAX_FILE_SIZE || quality <= 0.1) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              // Reduce quality and try again
              tryCompress(quality - 0.1);
            }
          },
          "image/jpeg",
          quality
        );
      };

      // Start with quality 0.9 and reduce if needed
      tryCompress(0.9);
    };

    img.onerror = () => reject(new Error("Errore nel caricamento dell'immagine"));
    img.src = URL.createObjectURL(file);
  });
};

const EventForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = id && id !== "nuovo";

  const [formData, setFormData] = useState<EventoFormData>({
    Titolo: "",
    DataEvento: "",
    Menu: "",
    Ingredienti: "",
    PrezzoAlcolico: 0,
    PrezzoAnalcolico: 0,
    AlcolicoDesc: "",
    AnalcolicoDesc: "",
    Prenotabile: true,
    PostiTotali: 0,
    Attivo: true,
    ImmagineUrl: "",
    MostraGiornoEsatto: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Image upload state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [compressingImage, setCompressingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadEvento = async () => {
      try {
        setLoading(true);
        const evento = await eventService.getEvento(parseInt(id!));
        setFormData({
          Titolo: evento.Titolo,
          DataEvento: formatDateForInput(evento.DataEvento),
          Menu: evento.Menu,
          Ingredienti: evento.Ingredienti || "",
          PrezzoAlcolico: evento.PrezzoAlcolico || 0,
          PrezzoAnalcolico: evento.PrezzoAnalcolico || 0,
          AlcolicoDesc: evento.AlcolicoDesc || "",
          AnalcolicoDesc: evento.AnalcolicoDesc || "",
          Prenotabile: evento.Prenotabile === true,
          PostiTotali: evento.PostiTotali,
          Attivo: evento.Attivo !== false,
          ImmagineUrl: evento.ImmagineUrl || "",
          MostraGiornoEsatto: evento.MostraGiornoEsatto !== false,
        });
        // Set the image preview if there's an existing image
        if (evento.ImmagineUrl) {
          setImagePreview(evento.ImmagineUrl);
        }
        setError(null);
      } catch (err) {
        setError("Errore nel caricamento dell'evento");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isEditing) {
      loadEvento();
    }
  }, [id, isEditing]);

  // Converts UTC date string to local time for the datetime-local input
  const formatDateForInput = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Converts local datetime-local input value to UTC ISO string for saving
  const convertLocalToUTC = (localDateString: string): string => {
    const localDate = new Date(localDateString);
    return localDate.toISOString();
  };

  // Handle image file selection
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        setError("Tipo di file non supportato. Usa JPG, PNG, WebP o GIF.");
        return;
      }
      
      let finalFile = file;
      
      // Auto-compress if file is too large
      if (file.size > MAX_FILE_SIZE) {
        try {
          setCompressingImage(true);
          setError(null);
          finalFile = await compressImage(file);
        } catch (err) {
          setError("Errore durante la compressione dell'immagine");
          setCompressingImage(false);
          return;
        } finally {
          setCompressingImage(false);
        }
      }
      
      setImageFile(finalFile);
      setImagePreview(URL.createObjectURL(finalFile));
      setError(null);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, ImmagineUrl: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    
    let newValue: string | number | boolean = value;
    if (type === "checkbox") {
      newValue = target.checked;
    } else if (type === "number") {
      newValue = value === "" ? 0 : parseFloat(value);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.Titolo.trim()) {
      setError("Il titolo è obbligatorio");
      return;
    }
    if (!formData.DataEvento) {
      setError("La data dell'evento è obbligatoria");
      return;
    }
    if (formData.PostiTotali <= 0) {
      setError("Il numero di posti deve essere maggiore di zero");
      return;
    }

    try {
      setSaving(true);
      
      let immagineUrl = formData.ImmagineUrl;
      
      // Convert local time to UTC before saving
      const dataToSave = {
        ...formData,
        DataEvento: convertLocalToUTC(formData.DataEvento),
        ImmagineUrl: immagineUrl,
      };
      
      let savedEvento;
      if (isEditing) {
        // If editing and there's a new image file, upload it first
        if (imageFile) {
          setUploadingImage(true);
          try {
            const uploadResult = await eventService.uploadEventImage(parseInt(id!), imageFile);
            immagineUrl = uploadResult.url;
            dataToSave.ImmagineUrl = immagineUrl;
          } finally {
            setUploadingImage(false);
          }
        }
        savedEvento = await eventService.updateEvento(parseInt(id!), dataToSave);
      } else {
        // For new events, create first then upload image
        savedEvento = await eventService.createEvento(dataToSave);
        
        // If there's an image file, upload it and update the event
        if (imageFile && savedEvento.EventoId) {
          setUploadingImage(true);
          try {
            const uploadResult = await eventService.uploadEventImage(savedEvento.EventoId, imageFile);
            // Update the event with the image URL
            await eventService.updateEvento(savedEvento.EventoId, {
              ...dataToSave,
              ImmagineUrl: uploadResult.url,
            });
          } finally {
            setUploadingImage(false);
          }
        }
      }
      navigate("/serate");
    } catch (err) {
      setError(
        isEditing
          ? "Errore nell'aggiornamento dell'evento"
          : "Errore nella creazione dell'evento"
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={10}>
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.500" />
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={8}>
      <Box bg="gray.800" p={8} borderRadius="xl" boxShadow="xl">
        <Heading size="lg" mb={6}>
          {isEditing ? "Modifica Evento" : "Nuovo Evento"}
        </Heading>

        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel>Titolo</FormLabel>
              <Input
                name="Titolo"
                value={formData.Titolo}
                onChange={handleChange}
                placeholder="Inserisci il titolo dell'evento"
                bg="gray.700"
              />
            </FormControl>

            {/* Image Upload Section */}
            <FormControl>
              <FormLabel>Immagine Evento</FormLabel>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageChange}
                ref={fileInputRef}
                display="none"
              />
              
              {imagePreview ? (
                <Box position="relative">
                  <Image
                    src={imagePreview}
                    alt="Anteprima immagine"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    mb={2}
                  />
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                    >
                      Cambia immagine
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={handleRemoveImage}
                    >
                      Rimuovi
                    </Button>
                  </HStack>
                </Box>
              ) : compressingImage ? (
                <Box
                  border="2px dashed"
                  borderColor="blue.500"
                  borderRadius="md"
                  p={6}
                  textAlign="center"
                >
                  <VStack spacing={2}>
                    <Spinner color="blue.500" />
                    <Text color="blue.400">
                      Compressione immagine in corso...
                    </Text>
                  </VStack>
                </Box>
              ) : (
                <Box
                  border="2px dashed"
                  borderColor="gray.600"
                  borderRadius="md"
                  p={6}
                  textAlign="center"
                  cursor="pointer"
                  _hover={{ borderColor: "gray.500", bg: "gray.750" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <VStack spacing={2}>
                    <Text color="gray.400">
                      Clicca per caricare un'immagine
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      JPG, PNG, WebP o GIF (auto-compressione se &gt; 5MB)
                    </Text>
                  </VStack>
                </Box>
              )}
              <FormHelperText>
                L'immagine sarà mostrata nella pagina dell'evento
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Data e Ora</FormLabel>
              <Input
                type="datetime-local"
                name="DataEvento"
                value={formData.DataEvento}
                onChange={handleChange}
                bg="gray.700"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Menu</FormLabel>
              <RichTextEditor
                value={formData.Menu}
                onChange={(value) => setFormData((prev) => ({ ...prev, Menu: value }))}
                placeholder="Descrivi il menu dell'evento"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Ingredienti</FormLabel>
              <RichTextEditor
                value={formData.Ingredienti}
                onChange={(value) => setFormData((prev) => ({ ...prev, Ingredienti: value }))}
                placeholder="Elenca gli ingredienti principali (es: latticini, glutine, frutta secca...)"
              />
            </FormControl>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel>Prezzo Alcolico (€)</FormLabel>
                <Input
                  type="number"
                  name="PrezzoAlcolico"
                  value={formData.PrezzoAlcolico}
                  onChange={handleChange}
                  min={0}
                  step={0.01}
                  bg="gray.700"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Prezzo Analcolico (€)</FormLabel>
                <Input
                  type="number"
                  name="PrezzoAnalcolico"
                  value={formData.PrezzoAnalcolico}
                  onChange={handleChange}
                  min={0}
                  step={0.01}
                  bg="gray.700"
                />
              </FormControl>
            </Grid>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel>Descrizione Alcolico</FormLabel>
                <Input
                  name="AlcolicoDesc"
                  value={formData.AlcolicoDesc}
                  onChange={handleChange}
                  placeholder="es. vino, birra artigianale"
                  bg="gray.700"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Descrizione Analcolico</FormLabel>
                <Input
                  name="AnalcolicoDesc"
                  value={formData.AnalcolicoDesc}
                  onChange={handleChange}
                  placeholder="es. acqua, succhi"
                  bg="gray.700"
                />
              </FormControl>
            </Grid>

            <FormControl isRequired>
              <FormLabel>Posti Totali</FormLabel>
              <Input
                type="number"
                name="PostiTotali"
                value={formData.PostiTotali || ""}
                onChange={handleChange}
                min={1}
                placeholder="Numero totale di posti disponibili"
                bg="gray.700"
              />
            </FormControl>

            <FormControl>
              <Checkbox
                name="MostraGiornoEsatto"
                isChecked={formData.MostraGiornoEsatto}
                onChange={handleChange}
                colorScheme="gray"
              >
                Mostra il giorno esatto
              </Checkbox>
              <FormHelperText>
                Se disattivato, nell'elenco pubblico delle serate verrà mostrato solo mese e anno (utile per annunciare eventi lontani senza rivelare già la data esatta)
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Checkbox
                name="Prenotabile"
                isChecked={formData.Prenotabile}
                onChange={handleChange}
                colorScheme="gray"
              >
                Prenotabile
              </Checkbox>
              <FormHelperText>
                Se disattivato, mostra "Prenotabile prossimamente"
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Checkbox
                name="Attivo"
                isChecked={formData.Attivo}
                onChange={handleChange}
                colorScheme="gray"
              >
                Evento Attivo
              </Checkbox>
              <FormHelperText>
                Se disattivato, l'evento non sarà visibile agli utenti
              </FormHelperText>
            </FormControl>

            <HStack spacing={4} pt={4}>
              <Button
                variant="ghost"
                onClick={() => navigate("/serate")}
                isDisabled={saving || uploadingImage || compressingImage}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={saving || uploadingImage || compressingImage}
                loadingText={compressingImage ? "Compressione..." : uploadingImage ? "Caricamento immagine..." : "Salvataggio..."}
              >
                {isEditing ? "Salva Modifiche" : "Crea Evento"}
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Container>
  );
};

export default EventForm;
